/* eslint-disable no-undef */
/**
 * Client-side cryptographic operations using the Web Crypto API (AES-GCM-256).
 * Ensures zero-knowledge encryption where the secret decryption key is stored
 * solely in the URL hash fragment and never sent to any server.
 */

function getNodeCrypto() {
  try {
    const req = typeof __non_webpack_require__ !== 'undefined'
      ? __non_webpack_require__
      : (typeof module !== 'undefined' && typeof module.require === 'function' ? module.require.bind(module) : null)
    if (req) {
      return req('crypto')
    }
  } catch (e) {
    // ignore
  }
  return null
}

function getCryptoSubtle() {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    return window.crypto.subtle
  }
  if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) {
    return globalThis.crypto.subtle
  }
  if (typeof global !== 'undefined' && global.crypto && global.crypto.subtle) {
    return global.crypto.subtle
  }
  const nc = getNodeCrypto()
  if (nc && nc.webcrypto && nc.webcrypto.subtle) {
    return nc.webcrypto.subtle
  }
  return null
}

function getRandomBytes(length) {
  const arr = new Uint8Array(length)
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.getRandomValues === 'function') {
    return window.crypto.getRandomValues(arr)
  }
  if (typeof globalThis !== 'undefined' && globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function') {
    return globalThis.crypto.getRandomValues(arr)
  }
  if (typeof global !== 'undefined' && global.crypto && typeof global.crypto.getRandomValues === 'function') {
    return global.crypto.getRandomValues(arr)
  }
  const nc = getNodeCrypto()
  if (nc && typeof nc.randomFillSync === 'function') {
    nc.randomFillSync(arr)
    return arr
  }
  return arr
}

function arrayBufferToBase64(buffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(binary)
  }
  if (typeof btoa === 'function') {
    return btoa(binary)
  }
  return Buffer.from(binary, 'binary').toString('base64')
}

function base64ToUint8Array(base64) {
  let binaryString
  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    binaryString = window.atob(base64)
  } else if (typeof atob === 'function') {
    binaryString = atob(base64)
  } else {
    binaryString = Buffer.from(base64, 'base64').toString('binary')
  }
  const len = binaryString.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes
}

function encodeUtf8(str) {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str)
  }
  return new Uint8Array(Buffer.from(str, 'utf-8'))
}

function decodeUtf8(buf) {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(buf)
  }
  return Buffer.from(buf).toString('utf-8')
}

/**
 * Generates a random 256-bit AES-GCM CryptoKey.
 */
export async function generateAesKey() {
  const subtle = getCryptoSubtle()
  if (!subtle) {
    throw new Error('Web Crypto API is not supported in this environment')
  }
  return await subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt']
  )
}

/**
 * Exports a CryptoKey to raw format and encodes it as Base64.
 */
export async function exportKeyToBase64(cryptoKey) {
  const subtle = getCryptoSubtle()
  const raw = await subtle.exportKey('raw', cryptoKey)
  return arrayBufferToBase64(raw)
}

/**
 * Imports a raw Base64-encoded key back into a CryptoKey for AES-GCM.
 */
export async function importKeyFromBase64(base64Key) {
  const subtle = getCryptoSubtle()
  const bytes = base64ToUint8Array(base64Key)
  return await subtle.importKey(
    'raw',
    bytes,
    {
      name: 'AES-GCM',
    },
    false,
    ['decrypt']
  )
}

/**
 * Encrypts a string plaintext with AES-GCM using a generated 12-byte IV.
 * @returns {Promise<{ ciphertext: string, iv: string }>} Base64-encoded ciphertext and IV.
 */
export async function encryptSecret(plaintext, cryptoKey) {
  const subtle = getCryptoSubtle()
  const iv = getRandomBytes(12)
  const encoded = encodeUtf8(plaintext)

  const ciphertextBuffer = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    cryptoKey,
    encoded
  )

  return {
    ciphertext: arrayBufferToBase64(ciphertextBuffer),
    iv: arrayBufferToBase64(iv.buffer || iv),
  }
}

/**
 * Decrypts a Base64-encoded ciphertext using the provided IV and CryptoKey.
 * @returns {Promise<string>} The decrypted UTF-8 plaintext.
 */
export async function decryptSecret(ciphertextBase64, ivBase64, cryptoKey) {
  const subtle = getCryptoSubtle()
  const ciphertextBytes = base64ToUint8Array(ciphertextBase64)
  const ivBytes = base64ToUint8Array(ivBase64)

  const decryptedBuffer = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ivBytes,
    },
    cryptoKey,
    ciphertextBytes
  )

  return decodeUtf8(decryptedBuffer)
}

/**
 * Parses secret ID and key from URL hash or query string.
 * Supports formats like #id=DOC_ID&key=AES_KEY_B64 or #DOC_ID:AES_KEY_B64
 */
export function parseSecretHash(hashOrUrl) {
  if (!hashOrUrl) return { id: null, key: null }

  let raw = hashOrUrl
  if (raw.includes('#')) {
    raw = raw.split('#')[1]
  } else if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('about:')) {
    return { id: null, key: null }
  }

  if (!raw) return { id: null, key: null }

  // Format 1: id=DOC_ID&key=KEY
  const params = new URLSearchParams(raw)
  const idParam = params.get('id')
  const keyParam = params.get('key')

  if (idParam && keyParam) {
    return { id: idParam, key: keyParam.replace(/ /g, '+') }
  }

  // Format 2: DOC_ID:KEY
  if (raw.includes(':') && !raw.includes('/')) {
    const parts = raw.split(':')
    if (parts.length === 2 && parts[0] && parts[1]) {
      return { id: parts[0], key: parts[1].replace(/ /g, '+') }
    }
  }

  return { id: null, key: null }
}
